import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap, useMapEvents, Marker } from 'react-leaflet';
import L from 'leaflet';
import { Plus, Pencil, Trash2, Save, X, MapPin, Flame, Undo2, Bike, Route } from 'lucide-react';
import { getAdminSystem, adminAddTrail, adminUpdateTrail, adminDeleteTrail, getAdminHotspots, getAdminRides, getAdminRideCoords } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import 'leaflet/dist/leaflet.css';

const DIFFICULTY_COLORS = {
  white: '#a3a19a',
  green: '#3a9e3a',
  blue: '#3178c6',
  black: '#2b2a28',
  dh: '#d4453a',
  unknown: '#a3a19a',
};

const DIFFICULTIES = ['white', 'green', 'blue', 'black', 'dh', 'unknown'];

const OSRM_BASE = 'https://router.project-osrm.org/route/v1/foot';

// Marker icons
const waypointIcon = L.divIcon({
  className: 'waypoint-marker',
  html: '<div class="waypoint-marker__dot"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const segmentIcon = (color) => L.divIcon({
  className: 'waypoint-marker',
  html: `<div class="waypoint-marker__dot" style="background:${color};width:18px;height:18px;border-width:3px"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Calculate length in km from array of [lng, lat] pairs
function calcLength(coords) {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1];
    const [lng2, lat2] = coords[i];
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return Math.round(total * 100) / 100;
}

// Route between two points via OSRM
async function routeBetween(from, to) {
  const url = `${OSRM_BASE}/${from[0]},${from[1]};${to[0]},${to[1]}?overview=full&geometries=geojson`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data.code === 'Ok' && data.routes?.[0]) {
      return data.routes[0].geometry.coordinates;
    }
  } catch { /* fall through */ }
  return [from, to];
}

// Find nearest point index on a polyline to a click location
function findNearestIndex(coords, latlng) {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const [lat, lng] = coords[i];
    const d = (lat - latlng.lat) ** 2 + (lng - latlng.lng) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, bounds]);
  return null;
}

// Click handler for different draw modes
function MapClickHandler({ mode, onClickMap }) {
  useMapEvents({
    click(e) {
      if (mode) onClickMap(e);
    },
  });
  return null;
}

export default function AdminTrailEditor() {
  const { id } = useParams();
  const toast = useToast();
  const confirm = useConfirm();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [editingTrail, setEditingTrail] = useState(null);

  // Mode: null | 'ride-pick' | 'ride-segment' | 'draw-waypoints' | 'draw-done'
  const [mode, setMode] = useState(null);

  // Ride-based drawing
  const [rides, setRides] = useState([]);
  const [ridesLoading, setRidesLoading] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null); // { coords: [[lat,lng],...], name, ... }
  const [rideLoading, setRideLoading] = useState(false);
  const [startIdx, setStartIdx] = useState(null);
  const [endIdx, setEndIdx] = useState(null);

  // Waypoint-based drawing (OSRM fallback)
  const [waypoints, setWaypoints] = useState([]);
  const [routedSegments, setRoutedSegments] = useState([]);
  const [routing, setRouting] = useState(false);

  // New trail form
  const [newTrailForm, setNewTrailForm] = useState({ name: '', difficulty: 'unknown' });
  const [saving, setSaving] = useState(false);

  // Hotspots
  const [hotspots, setHotspots] = useState([]);
  const [showHotspots, setShowHotspots] = useState(false);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);

  const loadSystem = useCallback(() => {
    getAdminSystem(id)
      .then(res => setSystem(res.data))
      .catch(() => toast('Failed to load system', 'error'))
      .finally(() => setLoading(false));
  }, [id, toast]);

  useEffect(loadSystem, [loadSystem]);

  function loadHotspots() {
    setHotspotsLoading(true);
    getAdminHotspots(id)
      .then(res => {
        setHotspots(res.data);
        setShowHotspots(true);
        if (res.data.length === 0) toast('No unmapped hotspots found', 'success');
        else toast(`Found ${res.data.length} hotspot${res.data.length === 1 ? '' : 's'}`, 'success');
      })
      .catch(() => toast('Failed to load hotspots', 'error'))
      .finally(() => setHotspotsLoading(false));
  }

  // Start ride-based creation
  function handleStartFromRide() {
    setMode('ride-pick');
    setSelectedTrail(null);
    setEditingTrail(null);
    setRidesLoading(true);
    getAdminRides()
      .then(res => setRides(res.data))
      .catch(() => toast('Failed to load rides', 'error'))
      .finally(() => setRidesLoading(false));
  }

  // Pick a specific ride
  async function handlePickRide(ride) {
    setRideLoading(true);
    try {
      const res = await getAdminRideCoords(ride.activityId);
      setSelectedRide({
        ...res.data,
        // coords are [lat, lng] from Strava
        coords: res.data.coordinates,
      });
      setMode('ride-segment');
      setStartIdx(null);
      setEndIdx(null);
    } catch {
      toast('Failed to load ride coordinates', 'error');
    } finally {
      setRideLoading(false);
    }
  }

  // Handle map click in ride-segment mode
  function handleMapClick(e) {
    if (mode === 'ride-segment' && selectedRide) {
      const idx = findNearestIndex(selectedRide.coords, e.latlng);
      if (startIdx === null) {
        setStartIdx(idx);
      } else if (endIdx === null) {
        // Ensure start < end
        if (idx === startIdx) return;
        const [s, en] = idx > startIdx ? [startIdx, idx] : [idx, startIdx];
        setStartIdx(s);
        setEndIdx(en);
      } else {
        // Reset and pick new start
        setStartIdx(idx);
        setEndIdx(null);
      }
    } else if (mode === 'draw-waypoints') {
      handleWaypointClick([e.latlng.lng, e.latlng.lat]);
    }
  }

  // Get the segment slice from ride coords (returns GeoJSON [lng, lat] format)
  const segmentCoords = useMemo(() => {
    if (!selectedRide || startIdx === null || endIdx === null) return null;
    const slice = selectedRide.coords.slice(startIdx, endIdx + 1);
    // Convert from [lat, lng] to [lng, lat] for GeoJSON
    return slice.map(([lat, lng]) => [lng, lat]);
  }, [selectedRide, startIdx, endIdx]);

  // Confirm segment and show form
  function handleConfirmSegment() {
    if (!segmentCoords || segmentCoords.length < 2) {
      toast('Select a start and end point on the ride', 'error');
      return;
    }
    setMode('draw-done');
  }

  // --- Waypoint-based drawing (fallback) ---
  const handleWaypointClick = useCallback(async (lngLat) => {
    if (routing) return;
    const newWaypoints = [...waypoints, lngLat];
    setWaypoints(newWaypoints);

    if (newWaypoints.length >= 2) {
      const from = newWaypoints[newWaypoints.length - 2];
      const to = newWaypoints[newWaypoints.length - 1];
      setRouting(true);
      try {
        const segment = await routeBetween(from, to);
        setRoutedSegments(prev => [...prev, segment]);
      } catch {
        setRoutedSegments(prev => [...prev, [from, to]]);
      } finally {
        setRouting(false);
      }
    }
  }, [routing, waypoints]);

  function handleUndoWaypoint() {
    if (waypoints.length <= 1) {
      setWaypoints([]);
      setRoutedSegments([]);
      return;
    }
    setWaypoints(prev => prev.slice(0, -1));
    setRoutedSegments(prev => prev.slice(0, -1));
  }

  const fullRoutedCoords = useMemo(() => {
    if (routedSegments.length === 0) return [];
    const coords = [...routedSegments[0]];
    for (let i = 1; i < routedSegments.length; i++) {
      coords.push(...routedSegments[i].slice(1));
    }
    return coords;
  }, [routedSegments]);

  function handleFinishWaypoints() {
    if (waypoints.length < 2) {
      toast('Click at least 2 points on the map', 'error');
      return;
    }
    setMode('draw-done');
  }

  // Get the final coordinates for saving (GeoJSON [lng, lat])
  const finalCoords = mode === 'draw-done'
    ? (segmentCoords || fullRoutedCoords)
    : null;

  // --- Save / Cancel ---
  async function handleSaveNewTrail() {
    if (!newTrailForm.name.trim()) {
      toast('Trail name is required', 'error');
      return;
    }
    if (!finalCoords || finalCoords.length < 2) {
      toast('No trail coordinates', 'error');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: newTrailForm.name.trim(),
        difficulty: newTrailForm.difficulty,
        length: calcLength(finalCoords),
        coordinates: { type: 'LineString', coordinates: finalCoords },
      };
      const res = await adminAddTrail(id, data);
      setSystem(res.data);
      handleCancelAll();
      toast('Trail added', 'success');
    } catch {
      toast('Failed to add trail', 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleCancelAll() {
    setMode(null);
    setRides([]);
    setSelectedRide(null);
    setStartIdx(null);
    setEndIdx(null);
    setWaypoints([]);
    setRoutedSegments([]);
    setNewTrailForm({ name: '', difficulty: 'unknown' });
  }

  async function handleSaveEdit() {
    if (!editingTrail) return;
    setSaving(true);
    try {
      const res = await adminUpdateTrail(id, editingTrail._id, {
        name: editingTrail.name,
        difficulty: editingTrail.difficulty,
        length: editingTrail.coordinates?.coordinates?.length > 1
          ? calcLength(editingTrail.coordinates.coordinates)
          : editingTrail.length,
        coordinates: editingTrail.coordinates,
      });
      setSystem(res.data);
      setEditingTrail(null);
      setSelectedTrail(null);
      toast('Trail updated', 'success');
    } catch {
      toast('Failed to update trail', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTrail(trailId, trailName) {
    const confirmed = await confirm(`Delete "${trailName}"? This affects all users.`);
    if (!confirmed) return;
    try {
      const res = await adminDeleteTrail(id, trailId);
      setSystem(res.data);
      setSelectedTrail(null);
      setEditingTrail(null);
      toast('Trail deleted', 'success');
    } catch {
      toast('Failed to delete trail', 'error');
    }
  }

  // --- Computed ---
  const trails = system?.trails || [];

  const mappableTrails = useMemo(
    () => trails.filter(t => t.coordinates?.coordinates?.length > 1),
    [trails]
  );

  const trailLines = useMemo(
    () => mappableTrails.map(t => ({
      ...t,
      positions: t.coordinates.coordinates.map(([lng, lat]) => [lat, lng]),
    })),
    [mappableTrails]
  );

  const bounds = useMemo(() => {
    if (trailLines.length === 0) return null;
    const allPoints = trailLines.flatMap(t => t.positions);
    const lats = allPoints.map(p => p[0]);
    const lngs = allPoints.map(p => p[1]);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }, [trailLines]);

  const trailsMissingCoords = trails.filter(
    t => !t.coordinates?.coordinates?.length || t.coordinates.coordinates.length < 2
  );

  const maxHotspotUsers = useMemo(
    () => Math.max(1, ...hotspots.map(h => h.userCount)),
    [hotspots]
  );

  // Ride polyline positions for map display (already [lat, lng])
  const ridePositions = selectedRide?.coords || [];

  // Segment highlight positions
  const segmentPositions = useMemo(() => {
    if (!selectedRide || startIdx === null || endIdx === null) return [];
    return selectedRide.coords.slice(startIdx, endIdx + 1);
  }, [selectedRide, startIdx, endIdx]);

  // Routed waypoint positions for display
  const routedPositions = useMemo(
    () => fullRoutedCoords.map(([lng, lat]) => [lat, lng]),
    [fullRoutedCoords]
  );

  if (loading) {
    return <div className="loading"><div className="spinner spinner--lg" /></div>;
  }

  if (!system) {
    return <div className="alert alert--error">System not found</div>;
  }

  const isDrawing = mode && mode !== 'ride-pick';
  const cursorStyle = (mode === 'ride-segment' || mode === 'draw-waypoints') ? 'crosshair' : '';

  return (
    <div>
      <Link to="/admin" className="back-link">&larr; Admin</Link>

      <div className="page-header">
        <h1 className="page-header__title">{system.name}</h1>
        <p className="page-header__subtitle">
          {system.region}{system.country ? `, ${system.country}` : ''} &middot; {trails.length} trails &middot; {mappableTrails.length} with coordinates
        </p>
      </div>

      {/* Map */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '24px' }}>
        <MapContainer
          bounds={bounds || [[46.5, -94], [46.6, -93.9]]}
          boundsOptions={{ padding: [40, 40] }}
          style={{ height: '500px', width: '100%', cursor: cursorStyle }}
          scrollWheelZoom={true}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
          {bounds && <FitBounds bounds={bounds} />}

          <MapClickHandler mode={mode === 'ride-segment' || mode === 'draw-waypoints' ? mode : null} onClickMap={handleMapClick} />

          {/* Existing trails */}
          {trailLines.map(trail => (
            <Polyline
              key={trail._id}
              positions={trail.positions}
              pathOptions={{
                color: selectedTrail === trail._id ? '#f0a622' : DIFFICULTY_COLORS[trail.difficulty] || DIFFICULTY_COLORS.unknown,
                weight: selectedTrail === trail._id ? 5 : 3,
                opacity: isDrawing ? 0.3 : 0.8,
              }}
              eventHandlers={{
                click: () => {
                  if (!mode) {
                    setSelectedTrail(trail._id);
                    setEditingTrail(null);
                  }
                },
              }}
            >
              {!isDrawing && (
                <Tooltip sticky>
                  <span style={{ fontWeight: 600 }}>{trail.name}</span>
                  <br />
                  <span>{trail.difficulty}{trail.length ? ` · ${trail.length} km` : ''}</span>
                </Tooltip>
              )}
            </Polyline>
          ))}

          {/* Hotspot markers */}
          {showHotspots && hotspots.map((hotspot, i) => {
            const intensity = hotspot.userCount / maxHotspotUsers;
            const radius = 8 + intensity * 16;
            return (
              <CircleMarker
                key={`hotspot-${i}`}
                center={[hotspot.lat, hotspot.lng]}
                radius={radius}
                pathOptions={{ color: '#d4453a', fillColor: '#d4453a', fillOpacity: 0.3 + intensity * 0.4, weight: 2 }}
              >
                <Tooltip>
                  <span style={{ fontWeight: 600 }}>Unmapped activity</span>
                  <br />
                  <span>{hotspot.userCount} {hotspot.userCount === 1 ? 'rider' : 'riders'} &middot; {hotspot.hitCount} passes</span>
                </Tooltip>
              </CircleMarker>
            );
          })}

          {/* Ride track overlay */}
          {selectedRide && ridePositions.length > 1 && (
            <Polyline
              positions={ridePositions}
              pathOptions={{ color: '#7c3aed', weight: 3, opacity: 0.5, dashArray: '8 6' }}
            />
          )}

          {/* Segment highlight */}
          {segmentPositions.length > 1 && (
            <Polyline
              positions={segmentPositions}
              pathOptions={{ color: '#f0a622', weight: 5, opacity: 0.9 }}
            />
          )}

          {/* Start/end markers on ride */}
          {selectedRide && startIdx !== null && (
            <Marker position={selectedRide.coords[startIdx]} icon={segmentIcon('#3a9e3a')} />
          )}
          {selectedRide && endIdx !== null && (
            <Marker position={selectedRide.coords[endIdx]} icon={segmentIcon('#d4453a')} />
          )}

          {/* Routed waypoint trail */}
          {routedPositions.length > 1 && (
            <Polyline positions={routedPositions} pathOptions={{ color: '#f0a622', weight: 4, opacity: 0.9 }} />
          )}

          {/* Waypoint markers */}
          {mode === 'draw-waypoints' && waypoints.map((wp, i) => (
            <Marker key={`wp-${i}`} position={[wp[1], wp[0]]} icon={waypointIcon} />
          ))}
        </MapContainer>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {!mode && (
          <>
            <button className="btn btn--primary" onClick={handleStartFromRide}>
              <Bike size={16} /> CREATE FROM RIDE
            </button>
            <button className="btn btn--secondary" onClick={() => { setMode('draw-waypoints'); setSelectedTrail(null); setEditingTrail(null); }}>
              <Route size={16} /> DRAW MANUALLY
            </button>
            <button
              className={`btn ${showHotspots ? 'btn--danger' : 'btn--secondary'}`}
              onClick={() => showHotspots ? setShowHotspots(false) : loadHotspots()}
              disabled={hotspotsLoading}
            >
              <Flame size={16} />
              {hotspotsLoading ? 'SCANNING...' : showHotspots ? 'HIDE HOTSPOTS' : 'SHOW HOTSPOTS'}
            </button>
          </>
        )}

        {/* Ride segment mode */}
        {mode === 'ride-segment' && (
          <>
            <div className="alert alert--info" style={{ margin: 0, flex: 1 }}>
              {startIdx === null
                ? 'Click a START point on the purple ride track.'
                : endIdx === null
                  ? 'Now click an END point on the ride track.'
                  : `Segment: ${segmentCoords?.length || 0} points · ${segmentCoords ? calcLength(segmentCoords) : 0} km — click to adjust, or confirm below.`}
            </div>
            {segmentCoords && (
              <button className="btn btn--primary" onClick={handleConfirmSegment}>CONFIRM SEGMENT</button>
            )}
            <button className="btn btn--secondary" onClick={handleCancelAll}><X size={16} /> CANCEL</button>
          </>
        )}

        {/* Manual draw mode */}
        {mode === 'draw-waypoints' && (
          <>
            <div className="alert alert--info" style={{ margin: 0, flex: 1 }}>
              {routing ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="spinner spinner--sm" /> Routing...
                </span>
              ) : waypoints.length === 0 ? (
                'Click on the map to place waypoints. Route will snap to existing paths.'
              ) : (
                `${waypoints.length} waypoint${waypoints.length !== 1 ? 's' : ''} · ${fullRoutedCoords.length > 1 ? calcLength(fullRoutedCoords) + ' km' : 'click next point'}`
              )}
            </div>
            {waypoints.length > 0 && (
              <button className="btn btn--secondary" onClick={handleUndoWaypoint} disabled={routing}>
                <Undo2 size={16} /> UNDO
              </button>
            )}
            {waypoints.length >= 2 && (
              <button className="btn btn--primary" onClick={handleFinishWaypoints} disabled={routing}>DONE</button>
            )}
            <button className="btn btn--secondary" onClick={handleCancelAll}><X size={16} /> CANCEL</button>
          </>
        )}
      </div>

      {/* Ride picker */}
      {mode === 'ride-pick' && (
        <div className="card mb-lg">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', marginBottom: '12px' }}>SELECT A RIDE</h3>
          <p className="text-muted" style={{ fontSize: '13px', marginBottom: '12px' }}>
            Pick a Strava ride, then select a start and end point to define the trail.
          </p>
          {ridesLoading ? (
            <div className="loading loading--sm"><span className="spinner" /></div>
          ) : rides.length === 0 ? (
            <p className="text-muted">No Strava rides found. Sync your rides first.</p>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <div className="grid grid--gap-sm">
                {rides.map(ride => (
                  <button
                    key={ride.activityId}
                    className="card trail-preview"
                    style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    onClick={() => handlePickRide(ride)}
                    disabled={rideLoading}
                  >
                    <div className="flex-1">
                      <span className="trail-preview__name">{ride.activityName || 'Untitled Ride'}</span>
                      <div className="trail-preview__meta">
                        <span>{new Date(ride.startDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {rideLoading && <span className="spinner spinner--sm" />}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button className="btn btn--secondary" onClick={handleCancelAll} style={{ marginTop: '12px' }}>
            <X size={16} /> CANCEL
          </button>
        </div>
      )}

      {/* New trail form (after segment confirmed or waypoints done) */}
      {mode === 'draw-done' && finalCoords && (
        <div className="card mb-lg">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', marginBottom: '12px' }}>NEW TRAIL</h3>
          <div className="form__row" style={{ marginBottom: '12px' }}>
            <div className="form__group" style={{ marginBottom: 0 }}>
              <label>Trail Name</label>
              <input
                value={newTrailForm.name}
                onChange={e => setNewTrailForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Timbershaft"
                autoFocus
              />
            </div>
            <div className="form__group" style={{ marginBottom: 0 }}>
              <label>Difficulty</label>
              <select
                value={newTrailForm.difficulty}
                onChange={e => setNewTrailForm(f => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map(d => (
                  <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            {finalCoords.length} points &middot; {calcLength(finalCoords)} km
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn--primary" onClick={handleSaveNewTrail} disabled={saving}>
              <Save size={16} /> {saving ? 'SAVING...' : 'SAVE TRAIL'}
            </button>
            <button className="btn btn--secondary" onClick={handleCancelAll}>CANCEL</button>
          </div>
        </div>
      )}

      {/* Hotspots legend */}
      {showHotspots && hotspots.length > 0 && (
        <div className="card mb-lg">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Flame size={16} color="var(--error-border)" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '16px' }}>UNMAPPED HOTSPOTS</h3>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Red circles show areas where riders frequently pass through but no trail is mapped.
          </p>
          <div className="grid grid--gap-sm">
            {hotspots.slice(0, 10).map((h, i) => (
              <div key={i} className="hotspot-item">
                <span className="hotspot-item__dot" />
                <span className="hotspot-item__label">
                  {h.userCount} {h.userCount === 1 ? 'rider' : 'riders'} &middot; {h.hitCount} passes
                </span>
                <span className="hotspot-item__coords">
                  {h.lat.toFixed(5)}, {h.lng.toFixed(5)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected trail detail */}
      {selectedTrail && !editingTrail && !mode && (() => {
        const trail = trails.find(t => t._id === selectedTrail);
        if (!trail) return null;
        return (
          <div className="card mb-lg">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px' }}>{trail.name}</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                  {trail.difficulty}{trail.length ? ` · ${trail.length} km` : ''}
                  {trail.coordinates?.coordinates?.length ? ` · ${trail.coordinates.coordinates.length} pts` : ' · No coordinates'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn--secondary btn--sm" onClick={() => setEditingTrail({ ...trail })}>
                  <Pencil size={14} /> EDIT
                </button>
                <button className="btn btn--danger btn--sm" onClick={() => handleDeleteTrail(trail._id, trail.name)}>
                  <Trash2 size={14} />
                </button>
                <button className="btn btn--secondary btn--sm" onClick={() => setSelectedTrail(null)}>
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit trail form */}
      {editingTrail && (
        <div className="card mb-lg">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', marginBottom: '12px' }}>EDIT TRAIL</h3>
          <div className="form__row" style={{ marginBottom: '12px' }}>
            <div className="form__group" style={{ marginBottom: 0 }}>
              <label>Trail Name</label>
              <input
                value={editingTrail.name}
                onChange={e => setEditingTrail(t => ({ ...t, name: e.target.value }))}
              />
            </div>
            <div className="form__group" style={{ marginBottom: 0 }}>
              <label>Difficulty</label>
              <select
                value={editingTrail.difficulty}
                onChange={e => setEditingTrail(t => ({ ...t, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map(d => (
                  <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn--primary" onClick={handleSaveEdit} disabled={saving}>
              <Save size={16} /> {saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
            <button className="btn btn--secondary" onClick={() => { setEditingTrail(null); setSelectedTrail(null); }}>
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Trails without coordinates */}
      {trailsMissingCoords.length > 0 && !mode && (
        <div style={{ marginTop: '24px' }}>
          <div className="section-header">
            <h2 className="section-header__title">
              <MapPin size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              MISSING COORDINATES
            </h2>
            <span className="section-header__meta">{trailsMissingCoords.length} trails</span>
          </div>
          <div className="grid grid--gap-md">
            {trailsMissingCoords.map(trail => (
              <div key={trail._id} className="card trail-preview" style={{ cursor: 'pointer' }}
                onClick={() => { setSelectedTrail(trail._id); setEditingTrail(null); }}>
                <span className="trail-preview__name">{trail.name}</span>
                <div className="trail-preview__meta">
                  <span className={`badge badge--${trail.difficulty}`}>{trail.difficulty}</span>
                  {trail.length > 0 && <span className="trail-preview__length">{trail.length} km</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All trails list */}
      {!mode && (
        <div style={{ marginTop: '24px' }}>
          <div className="section-header">
            <h2 className="section-header__title">ALL TRAILS</h2>
            <span className="section-header__meta">{trails.length} total</span>
          </div>
          <div className="grid grid--gap-sm">
            {trails.map(trail => (
              <div
                key={trail._id}
                className={`card trail-preview${selectedTrail === trail._id ? ' trail-preview--selected' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => { setSelectedTrail(trail._id); setEditingTrail(null); }}
              >
                <span className="trail-preview__name">{trail.name}</span>
                <div className="trail-preview__meta">
                  <span className={`badge badge--${trail.difficulty}`}>{trail.difficulty}</span>
                  {trail.length > 0 && <span className="trail-preview__length">{trail.length} km</span>}
                  {trail.coordinates?.coordinates?.length > 1 ? (
                    <span style={{ fontSize: '11px', color: 'var(--accent)' }}>mapped</span>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--warning-border)' }}>no coords</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
